import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CountdownTimer from '../components/CountdownTimer';
import { supabase } from '../lib/supabase';

interface HomepageImage {
  id: string;
  image_url: string;
  top_pos: string | null;
  left_pos: string | null;
  right_pos: string | null;
  bottom_pos: string | null;
  rotation: string;
  width_val: string;
  height_val: string;
  z_index_val: number;
}

const HomePage: React.FC = () => {
  const [images, setImages] = useState<HomepageImage[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Wedding date: August 2, 2025
  const weddingDate = new Date('2025-08-02T16:00:00');

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const { data, error } = await supabase
          .from('homepage_images')
          .select('*')
          .order('z_index_val');
          
        if (error) throw error;
        setImages(data || []);
      } catch (error) {
        console.error('Error fetching homepage images:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const titleVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20
      }
    }
  };

  const quoteVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8, rotate: 0 },
    visible: (custom: number) => ({
      opacity: 1,
      scale: 1,
      rotate: custom,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20,
        delay: custom * 0.1
      }
    })
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero Section */}
      <div className="h-screen relative bg-wedding-blue-900 overflow-hidden">
        {/* Photo Gallery */}
        <div className="absolute inset-0 opacity-80">
          {images.map((image, index) => (
            <motion.img
              key={image.id}
              src={image.image_url}
              alt={`Wedding Photo ${index + 1}`}
              className="absolute object-contain rounded-lg shadow-xl hover:scale-105 transition-transform duration-300 max-w-[90vw] md:max-w-[50vw]"
              style={{
                top: image.top_pos || undefined,
                left: image.left_pos || undefined,
                right: image.right_pos || undefined,
                bottom: image.bottom_pos || undefined,
                width: image.width_val,
                height: 'auto',
                transform: `rotate(${image.rotation})`,
                zIndex: image.z_index_val,
              }}
              variants={imageVariants}
              custom={parseFloat(image.rotation)}
              initial="hidden"
              animate="visible"
              whileHover={{ scale: 1.05, rotate: 0 }}
            />
          ))}
        </div>

        {/* Overlay */}
        <motion.div 
          className="absolute inset-0 bg-black bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ duration: 1 }}
        />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 z-10">
          <motion.div
            className="text-center"
            variants={titleVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h1 
              className="text-3xl md:text-5xl lg:text-6xl font-playfair mb-4"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              Kaisson & Laryssa
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-2xl italic mb-8 font-playfair"
              variants={fadeInUpVariants}
            >
              02 de Agosto de 2025
            </motion.p>
            
            <motion.div 
              className="w-32 h-0.5 bg-white mx-auto mb-8"
              initial={{ width: 0 }}
              animate={{ width: "8rem" }}
              transition={{ duration: 1, delay: 0.5 }}
            />
            
            <motion.div
              variants={quoteVariants}
              initial="hidden"
              animate="visible"
              className="max-w-xl mx-auto"
            >
              <motion.p 
                className="text-lg md:text-xl italic font-playfair"
                whileHover={{ scale: 1.02 }}
              >
                "O amor é paciente, o amor é bondoso... Tudo sofre, tudo crê, tudo espera, tudo suporta. O amor jamais acaba."
              </motion.p>
              <motion.p 
                className="text-sm mt-2 italic"
                variants={fadeInUpVariants}
              >
                1 Coríntios 13:4,7-8
              </motion.p>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Countdown Section */}
      <motion.section 
        className="py-16 bg-wedding-beige-100"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="container mx-auto px-4">
          <CountdownTimer targetDate={weddingDate} />
        </div>
      </motion.section>
      
      {/* About Section */}
      <motion.section 
        className="py-16"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            className="text-center mb-12"
            variants={fadeInUpVariants}
          >
            <motion.h2 
              className="text-3xl font-playfair font-semibold text-wedding-blue-800 mb-2"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              Nosso Grande Dia
            </motion.h2>
            <motion.div 
              className="w-24 h-1 bg-wedding-beige-400 mx-auto mt-4"
              initial={{ width: 0 }}
              animate={{ width: "6rem" }}
              transition={{ duration: 1 }}
            />
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              variants={fadeInUpVariants}
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-playfair font-medium text-wedding-blue-700 mb-3">
                A Cerimônia
              </h3>
              <p className="mb-4 text-gray-700">
                Teremos o prazer de celebrar nosso amor com uma cerimônia especial que começará às 9:30h. Você é nosso convidado especial para testemunhar este momento único em nossas vidas.
              </p>
              <p className="text-sm text-gray-600">
                <strong>Data:</strong> 02 de Agosto de 2025<br />
                <strong>Horário:</strong> 09:30<br />
                <strong>Local:</strong> Igreja Matriz de Capanema
              </p>
            </motion.div>
            
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              variants={fadeInUpVariants}
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-playfair font-medium text-wedding-blue-700 mb-3">
                A Recepção
              </h3>
              <p className="mb-4 text-gray-700">
                Após a cerimônia, continuaremos a celebração com uma festa especial. Será um momento de alegria, música e comemoração do nosso amor.
              </p>
              <p className="text-sm text-gray-600">
                <strong>Horário:</strong> 11:00<br />
                <strong>Local:</strong> Sítio Nosso lugar<br />
                <strong>Traje:</strong> Roupas leve e de banho (com modéstia)
              </p>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Spotify Playlist Section */}
      <motion.section
        className="py-16 bg-wedding-beige-50"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            className="text-center mb-8"
            variants={fadeInUpVariants}
          >
            <h2 className="text-2xl font-playfair font-semibold text-wedding-blue-800 mb-2">
              Nossa Playlist
            </h2>
            <p className="text-gray-600">
              Músicas que marcam nossa história de amor
            </p>
          </motion.div>

          <div className="bg-white rounded-lg shadow-lg p-4">
            <iframe 
              style={{ borderRadius: '12px' }}
              src="https://open.spotify.com/embed/playlist/4xKhkUBOXdpK1QEZvlOUCH?utm_source=generator" 
              width="100%" 
              height="152" 
              frameBorder="0" 
              allowFullScreen="" 
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
              loading="lazy"
            />
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <motion.footer 
        className="py-4 text-center text-sm text-gray-500"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        Desenvolvido por Kaisson Ferreira (VDC.IA) @2025
      </motion.footer>
    </div>
  );
};

export default HomePage;